/**
 * MetricTile — a KPI tile with an optional period-over-period micro-trend chip (the ERP-grade extension of
 * the plain KpiTile). label + big mono value + a delta chip ("+3 ▲ 12 %") tinted by whether the move is
 * good or bad. The "good direction" is metric-dependent (more labs = good; more churn = bad), so the caller
 * passes `goodWhen` ("up" | "down"); the chip resolves success/danger from the actual direction vs that.
 *
 * Color is never the only signal — the chip always carries a directional glyph (▲ ▼ —) + the signed number,
 * so it reads under daltonisme + AA. All token-driven (no raw hex, L-2). Numerics ride .nv-ltr-island so they
 * read identically FR/AR. Mirrors the existing management KpiTile structure so the grid stays visually flush.
 */
import type { ReactNode } from "react";
import { Card } from "@/ui";
import type { Delta, DeltaDirection } from "../metrics.lib";

export type MetricTone = "neutral" | "accent" | "success" | "warning" | "danger";

const TONE_VAR: Record<MetricTone, string> = {
  neutral: "var(--nv-text-0)",
  accent: "var(--nv-accent, var(--nv-azure-600))",
  success: "var(--nv-success, var(--nv-normal))",
  warning: "var(--nv-warning, var(--nv-high))",
  danger: "var(--nv-danger, var(--nv-critical))",
};

const DIR_GLYPH: Record<DeltaDirection, string> = { up: "▲", down: "▼", flat: "—" };

export interface MetricTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: MetricTone;
  icon?: ReactNode;
  /** The period-over-period delta to chip. Omit to render a plain tile (no trend). */
  delta?: Delta;
  /** Which direction is "good" for this metric — flips the chip success/danger. Default "up". */
  goodWhen?: "up" | "down";
  /** Short context label after the delta (e.g. "vs mois dernier"). */
  deltaContext?: string;
}

/** Resolve the chip foreground: a flat delta is neutral; otherwise good→success, bad→danger. */
function chipColor(dir: DeltaDirection, goodWhen: "up" | "down"): string {
  if (dir === "flat") return "var(--nv-text-3, var(--nv-steel))";
  const isGood = dir === goodWhen;
  return isGood ? "var(--nv-success, var(--nv-normal))" : "var(--nv-danger, var(--nv-critical))";
}

export function MetricTile({
  label,
  value,
  hint,
  tone = "neutral",
  icon,
  delta,
  goodWhen = "up",
  deltaContext,
}: MetricTileProps) {
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-2)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--nv-s-2)",
            color: "var(--nv-text-2, var(--nv-slate))",
            fontSize: "var(--nv-fs-label)",
          }}
        >
          {icon && <span aria-hidden>{icon}</span>}
          <span>{label}</span>
        </div>

        <div
          className="nv-ltr-island"
          style={{
            fontSize: "var(--nv-fs-display, var(--nv-fs-h1))",
            fontFamily: "var(--nv-font-mono)",
            fontWeight: 600,
            color: TONE_VAR[tone],
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>

        {delta && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--nv-s-2)",
              fontSize: "var(--nv-fs-caption)",
            }}
          >
            <span
              className="nv-ltr-island"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--nv-s-1)",
                fontWeight: 600,
                color: chipColor(delta.direction, goodWhen),
              }}
            >
              <span aria-hidden>{DIR_GLYPH[delta.direction]}</span>
              <span>
                {delta.diff > 0 ? "+" : ""}
                {delta.diff}
                {delta.pct != null ? ` (${delta.pct > 0 ? "+" : ""}${delta.pct} %)` : ""}
              </span>
            </span>
            {deltaContext && (
              <span style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>{deltaContext}</span>
            )}
          </div>
        )}

        {hint && (
          <div style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {hint}
          </div>
        )}
      </div>
    </Card>
  );
}
